import * as Dtos from "@/api/dtos";
import { PlaneTicket, Registration } from "@/api/dtos";
import { faChevronDown } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	Box,
	FormControlLabel,
	Grid,
	Radio,
	Tooltip,
	Typography,
	styled,
} from "@mui/material";
import {
	Checkbox,
	DatePicker,
	DateTimePicker,
	InputOnlyField,
	RadioGroup,
	Select,
	Text,
	ToggleButtonGroup,
} from "@ngt/forms";
import { Condition, ConditionMode, useScopedField } from "@ngt/forms-core";
import { FC } from "react";
import { ErrorFields } from "../admin/AdminRegistrationSteps";
import {
	StyledAccordianSummary,
	StyledAccordion,
	StyledAccordionDetails,
} from "./SpeakerForm";
import SpeakerTicketsSection from "./SpeakerTicketsSection";

const StyledTypography = styled(Typography)(({ theme }) => ({
	paddingBottom: theme.spacing(2),
}));

interface SpeakerTravelSectionProps {
	disableFlightPreferences?: boolean;
}

const columnMapping = {
	xs: 12,
	sm: 6,
	md: 3,
};



interface TravelSectionContentProps extends SpeakerTravelSectionProps {
	isDomesticDelegate: boolean;
}

const hasCountryConditionFn = ({ value }: { value?: Registration | null }) =>
						!!value?.postalCountry || !!value?.country;
const hasNoCountryConditionFn = ({ value }: { value?: Registration | null }) =>
						!value?.postalCountry && !value?.country;
const sharingWithCompanionConditionFn = ({ value }: { value?: Registration | null }) =>
						!!(value?.travelWithCompanion === 1);

const valueSubscription = { value: true };

//#region Unified Travel Section
const TravelSectionContent: FC<TravelSectionContentProps> = ({
	disableFlightPreferences,
	isDomesticDelegate,
}) => {
	const {
		state: { value: travelArrangementsNotRequired },
	} = useScopedField("travelArrangementsNotRequired", { value: true });

	const {
		state: { value: cabCharge },
	} = useScopedField("cabCharge");

	const {
		state: { value: registrationAffiliation },
	} = useScopedField<number>("registrationAffiliation", { value: true });

	// Computed text fragments based on affiliation and location
	const isInternationalSpeaker = !isDomesticDelegate && registrationAffiliation === Dtos.RegistrationAffiliation.GuestSpeaker;
	const isEducationGrant = registrationAffiliation === Dtos.RegistrationAffiliation.EducationTravelGrant;
	const isTraineeGrant = registrationAffiliation === Dtos.RegistrationAffiliation.TraineeAndEarlyCareersDayGrant;
	const isCAP = registrationAffiliation === Dtos.RegistrationAffiliation.CAP;

	const recipientTitle = 
		registrationAffiliation === Dtos.RegistrationAffiliation.Board ? "directors" :
		registrationAffiliation === Dtos.RegistrationAffiliation.CAP ? "BCT CAP members" :
		registrationAffiliation === Dtos.RegistrationAffiliation.GuestSpeaker ? "ASM guest speakers" :
		registrationAffiliation === Dtos.RegistrationAffiliation.TraineeAndEarlyCareersDayGrant ? "grant recipients" :
		registrationAffiliation === Dtos.RegistrationAffiliation.EducationTravelGrant ? "grant recipients" :
		"valued attendees";

	const registrationType = 
		isTraineeGrant ? "T&EC registration" :
		isEducationGrant ? "registration" :
		isInternationalSpeaker ? "speaker conference registration" :
		"conference registration";

	const flightClass = isInternationalSpeaker ? "business class" : "Qantas economy";
	const flightPreferenceNote = isInternationalSpeaker 
		? " (please note, BCT's preferred airline is Qantas and its subsidiary groups)" 
		: "";
	const groundTransferType = isInternationalSpeaker ? " (private car)" : isDomesticDelegate ? " (cab charge)" : "";

	const accommodationType = isTraineeGrant ? "around the T&EC day" : "during the conference";
	const additionalNotes = isEducationGrant ? " up to the value of $1500" : "";
	const specialGrantInfo = isTraineeGrant 
		? " Should you wish to also attend the BCT ASM (Wed 29-Fri 31) BCT will arrange flights around the conference and T&EC day." 
		: "";

	const arrivalDescription = isTraineeGrant ?
		"on" :
		isInternationalSpeaker ? 
		"before" :
		"on or before";
	const arrivalDate = isEducationGrant ? 
		"Wednesday 29th" : 
		isTraineeGrant ? 
			"Friday 31st" :
			"Tuesday 28th";
	const arrivalEvent = isEducationGrant ? 
		"the Welcome Scientific Session (3.30pm) and Poster Presentation event (5.00pm)" : 
		isTraineeGrant ?
			"the T&EC Dinner on Friday night" :
			isCAP ? 
				null : 
				isInternationalSpeaker ? 
					"the BCT Q&A event (5:30 PM)" : 
					"the Q&A event (5:30 PM)";

	return (
		<>
			{/* Introductory Text - dynamically generated */}
			<StyledTypography variant="body1">
				For {recipientTitle} BCT will support {registrationType}, return {flightClass} flights{flightPreferenceNote}, ground transfers{groundTransferType} and accommodation {accommodationType}{additionalNotes}. These bookings will be made by BCT and confirmed by the ASM Secretariat.{specialGrantInfo}
			</StyledTypography>

			{isDomesticDelegate && (
				<StyledTypography variant="body1">
					BCT will source the most economical flights for travel to and from Brisbane, QLD. Please use your digital cab charge card for transport to and from the airport. Alternatively, BCT will reimburse up to $100 for airport parking.
				</StyledTypography>
			)}

			<StyledTypography variant="body1">
				Arrival {arrivalDescription} {arrivalDate} of July is preferred{arrivalEvent ? ` in order to attend ${arrivalEvent}` : ""}. Please ensure all requested travel information is submitted by March 31st 2026 to allow sufficient time for flight arrangements to be made by BCT.
			</StyledTypography>

			{isInternationalSpeaker && (
				<StyledTypography>
					A private car will be arranged to transfer you and any guests you are travelling with, to and from Brisbane airport and Sofitel Brisbane Central.
				</StyledTypography>
			)}
			
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
				}}
			>
				<Grid container spacing={4} mt={isDomesticDelegate ? 2 : 0}>
					{/* Local Resident Section - only for local residents */}
					{isDomesticDelegate && (
						<>
							<Grid item sm={12}>
								<Grid item xs={12} md={8}>
									<Typography variant="h2">Local Resident</Typography>
								</Grid>

								<Grid item mt={1} mb={4}>
									<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
										<Typography>
											I am a local Brisbane resident and I <strong>do not require</strong>&nbsp;
											<strong>travel arrangements</strong> for the ASM
										</Typography>
										<InputOnlyField
											propertyPath="travelArrangementsNotRequired"
											component={Checkbox}
											FormControlProps={{
												fullWidth: false
											}}
										/>
									</Box>
								</Grid>

								<Grid
									item
									xs={12}
									md={4}
									sx={{ mt: { xs: 2, md: 0 } }}
								></Grid>
								<Typography variant="h2">Cab Charge</Typography>
								<Grid container columnSpacing={1} mt={4}>
									<Grid item xs={12} md={8}>
										<Typography color={"inherit"}>
											Do you require a Cab Charge?
										</Typography>
									</Grid>
									<Grid
										item
										xs={12}
										md={4}
										sx={{ mt: { xs: 2, md: 0 } }}
									>
										<InputOnlyField
											propertyPath="cabCharge"
											component={ToggleButtonGroup}
											sx={{ margin: "normal" }}
											disabled={travelArrangementsNotRequired}
										/>
									</Grid>
								</Grid>

								<Grid>
									<InputOnlyField
										label="Cab Charge Sent"
										propertyPath="cabChargeSent"
										component={Checkbox}
										sx={{ margin: "normal" }}
										disabled={!cabCharge || travelArrangementsNotRequired}
									/>
								</Grid>

								<Grid item xs={12} md={8} pt={4}>
									<Typography color={"inherit"}>
										Cab Charge Delivery Method
									</Typography>
								</Grid>
								<Box
									sx={{
										display: "flex",
										flexDirection: "column",
										pt: 2,
										pd: 4,
									}}
								>
									<InputOnlyField
										component={RadioGroup}
										propertyPath="cabChargeOption"
									>
										<FormControlLabel
											disabled={!cabCharge || travelArrangementsNotRequired}
											property="cabChargeOption"
											value={1}
											control={<Radio color="primary" />}
											label="SMS"
										/>
										<FormControlLabel
											disabled={!cabCharge || travelArrangementsNotRequired}
											value={2}
											control={<Radio color="primary" />}
											label="Email"
											property="cabChargeOption"
										/>
									</InputOnlyField>
								</Box>
							</Grid>
						</>
					)}

					{/* Frequent Flyer Numbers - common to both */}
					<Grid item sm={12}>
						<Typography variant="h2">Frequent Flyer Numbers</Typography>
					</Grid>
					<Grid item {...columnMapping}>
						<InputOnlyField
							label="Qantas"
							propertyPath="frequentFlyerNumberQantas"
							component={Text}
							variant="standard"
							margin="normal"
							disabled={travelArrangementsNotRequired}
						/>
					</Grid>
					<Grid item {...columnMapping}>
						<InputOnlyField
							label="Virgin"
							propertyPath="frequentFlyerNumberVirgin"
							component={Text}
							variant="standard"
							margin="normal"
							disabled={travelArrangementsNotRequired}
						/>
					</Grid>
					<Grid item {...columnMapping}>
						<InputOnlyField
							label="Air New Zealand"
							propertyPath="frequentFlyerNumberAirNZ"
							component={Text}
							variant="standard"
							margin="normal"
							disabled={travelArrangementsNotRequired}
						/>
					</Grid>
					<Grid item {...columnMapping}>
						<InputOnlyField
							label="Other Airline"
							propertyPath="frequentFlyerOther"
							component={Checkbox}
							sx={{ margin: "normal" }}
							disabled={travelArrangementsNotRequired}
						/>
					</Grid>

					<Condition
						condition={({ value }) =>
							!!value?.frequentFlyerOther
						}
						subscription={{
							value: true,
						}}
						mode={ConditionMode.Enable}
					>
						<Grid item {...columnMapping}>
							<InputOnlyField
								label="Airline Name"
								propertyPath="frequentFlyerNameOther"
								component={Text}
								variant="standard"
								margin="normal"
								disabled={travelArrangementsNotRequired}
							/>
						</Grid>
						<Grid item {...columnMapping}>
							<InputOnlyField
								label="Frequent Flyer Number"
								propertyPath="frequentFlyerNumberOther"
								component={Text}
								variant="standard"
								margin="normal"
								disabled={travelArrangementsNotRequired}
							/>
						</Grid>
					</Condition>

					{/* Flight Information - common to both */}
					<Grid item sm={12}>
						<Typography variant="h2">Flight Information</Typography>
					</Grid>

					<Grid item {...columnMapping}>
						<InputOnlyField
							label="Seating Preference"
							propertyPath="seatingPreference"
							component={Select}
							FormControlProps={{
								variant: "standard",
							}}
							sx={{ marginTop: "16px", marginBottom: "8px" }}
							disabled={travelArrangementsNotRequired}
						/>
					</Grid>
					<Grid item {...columnMapping}>
						<InputOnlyField
							label="Departure Date"
							propertyPath="departureDate"
							component={DatePicker}
							slotProps={{
								textField: {
									variant: "standard",
									margin: "normal",
								},
							}}
							disabled={travelArrangementsNotRequired}
						/>
					</Grid>
					<Grid item {...columnMapping}>
						<InputOnlyField
							label="Departure City"
							propertyPath="departureCity"
							component={Text}
							variant="standard"
							margin="normal"
							disabled={travelArrangementsNotRequired}
						/>
					</Grid>
					<Grid item {...columnMapping}>
						<Tooltip title="Hotel check in is 3pm, an early check in is not guaranteed.">
							<Box>
								<InputOnlyField
									label="Desired Arrival Time"
									propertyPath="desiredArrivalTime"
									component={DateTimePicker}
									slotProps={{
										textField: {
											variant: "standard",
											margin: "normal",
										},
									}}
									disabled={travelArrangementsNotRequired}
								/>
							</Box>
						</Tooltip>
					</Grid>
					<Grid item {...columnMapping}>
						<InputOnlyField
							label="Return Date"
							propertyPath="returnDate"
							component={DatePicker}
							slotProps={{
								textField: {
									variant: "standard",
									margin: "normal",
								},
							}}
							disabled={travelArrangementsNotRequired}
						/>
					</Grid>
					<Grid item {...columnMapping}>
						<InputOnlyField
							label="Return City"
							propertyPath="returnCity"
							component={Text}
							variant="standard"
							margin="normal"
							disabled={travelArrangementsNotRequired}
						/>
					</Grid>
					<Grid item {...columnMapping}>
						<Tooltip title="Hotel check out is 11am, a late check out is not guaranteed.">
							<Box>
								<InputOnlyField
									label="Desired Return Time"
									propertyPath="desiredReturnTime"
									component={DateTimePicker}
									slotProps={{
										textField: {
											variant: "standard",
											margin: "normal",
										},
									}}
									disabled={travelArrangementsNotRequired}
								/>
							</Box>
						</Tooltip>
					</Grid>

					<Grid
						item
						{...{
							xs: 12,
							sm: 8,
							md: 8,
						}}
					>
						<Typography color={isDomesticDelegate ? "inherit" : undefined}>
							Checked Baggage?
						</Typography>
					</Grid>
					<Grid
						item
						{...{
							xs: 12,
							sm: 4,
							md: 4,
						}}
					>
						<InputOnlyField
							propertyPath="checkedBaggage"
							component={ToggleButtonGroup}
							sx={{ margin: "normal" }}
							disabled={travelArrangementsNotRequired}
						/>
					</Grid>

					{/* Tickets - common to both */}
					<SpeakerTicketsSection />
					{/* Companion Section - slightly different text */}
					<Grid item sm={12}>
						<Typography variant="h2">Companion</Typography>
					</Grid>

					<Grid item sm={12}>
						<Typography variant="body1">
							{isDomesticDelegate
								? "Companion travel is not supported by BCT. If you would like your companion to join you for the conference dinner, please advise the ASM secretariat."
								: "Companion travel is not supported by BCT."}
						</Typography>
					</Grid>

					<Grid
						item
						{...{
							xs: 12,
							sm: 8,
							md: 8,
						}}
					>
						<Typography color={isDomesticDelegate ? "inherit" : undefined}>
							Are you travelling with a companion?
						</Typography>
					</Grid>
					<Grid
						item
						{...{
							xs: 12,
							sm: 4,
							md: 4,
						}}
					>
						<InputOnlyField
							propertyPath="travelWithCompanion"
							component={ToggleButtonGroup}
							sx={{ margin: "normal" }}
							disabled={travelArrangementsNotRequired}
						/>
					</Grid>

					<Condition
						condition={sharingWithCompanionConditionFn}
						subscription={valueSubscription}
						mode={ConditionMode.Show}
					>
						<Grid item {...columnMapping}>
							<InputOnlyField
								label="First Name"
								propertyPath="companionFirstName"
								component={Text}
								variant="standard"
								margin="normal"
								disabled={travelArrangementsNotRequired}
							/>
						</Grid>
						<Grid item {...columnMapping}>
							<InputOnlyField
								label="Companion Last Name"
								propertyPath="companionLastName"
								component={Text}
								variant="standard"
								margin="normal"
								disabled={travelArrangementsNotRequired}
							/>
						</Grid>

						{/* International-only fields */}
						{!isDomesticDelegate && (
							<>
								<Grid item {...columnMapping}>
									<InputOnlyField
										label="Companion Dietary Requirements"
										propertyPath="companionDietaryRequirements"
										component={Text}
										variant="standard"
										margin="normal"
										disabled={travelArrangementsNotRequired}
									/>
								</Grid>
								<Grid
									item
									{...{
										xs: 12,
										sm: 8,
										md: 8,
									}}
								>
									<Typography>
										Would you like your companion to attend the Faculty
										Dinner?
									</Typography>
								</Grid>
								<Grid
									item
									{...{
										xs: 12,
										sm: 4,
										md: 4,
									}}
								>
									<InputOnlyField
										propertyPath="companionAttendFacultyDinner"
										component={ToggleButtonGroup}
										sx={{ margin: "normal" }}
										disabled={travelArrangementsNotRequired}
									/>
								</Grid>

								<Grid
									item
									{...{
										xs: 12,
										sm: 8,
										md: 8,
									}}
								>
									<Typography>
										Would you like your companion to attend the Conference
										Dinner?
									</Typography>
								</Grid>
								<Grid
									item
									{...{
										xs: 12,
										sm: 4,
										md: 4,
									}}
								>
									<InputOnlyField
										propertyPath="companionAttendConferenceDinner"
										component={ToggleButtonGroup}
										sx={{ margin: "normal" }}
										disabled={travelArrangementsNotRequired}
									/>
								</Grid>
							</>
						)}
						<Condition
							condition={sharingWithCompanionConditionFn}
							subscription={valueSubscription}
							mode={ConditionMode.Show}
						>
							<Grid
								item
								{...{
									xs: 12,
									sm: 8,
									md: 8,
								}}
							>
								<Typography color={isDomesticDelegate ? "inherit" : undefined}>
									Do you require twin beds?
								</Typography>
							</Grid>
							<Grid
								item
								{...{
									xs: 12,
									sm: 4,
									md: 4,
								}}
							>
								<InputOnlyField
									propertyPath="companionTwinBeds"
									component={ToggleButtonGroup}
									sx={{ margin: "normal" }}
									disabled={travelArrangementsNotRequired}
								/>
							</Grid>
						</Condition>
						<Grid
							item
							{...{
								xs: 12,
								sm: 8,
								md: 8,
							}}
						>
							<Typography color={isDomesticDelegate ? "inherit" : undefined}>
								Will this companion share your accommodation for the
								duration of the accommodation?
							</Typography>
						</Grid>
						<Grid
							item
							{...{
								xs: 12,
								sm: 4,
								md: 4,
							}}
						>
							<InputOnlyField
								propertyPath="companionSharedAccommodation"
								component={ToggleButtonGroup}
								sx={{ margin: "normal" }}
								disabled={travelArrangementsNotRequired}
							/>
						</Grid>

						<Condition
							condition={({ value }) =>
								!(value?.companionSharedAccommodation === 1)
							}
							subscription={valueSubscription}
							mode={ConditionMode.Show}
						>
							<Grid item {...columnMapping}>
								<InputOnlyField
									label="Companion Check In Date"
									propertyPath="companionCheckInDate"
									component={DatePicker}
									slotProps={{
										textField: {
											variant: "standard",
											margin: "normal",
										},
									}}
									disabled={travelArrangementsNotRequired}
								/>
							</Grid>
							<Grid item {...columnMapping}>
								<InputOnlyField
									label="Companion Check Out Date"
									propertyPath="companionCheckOutDate"
									component={DatePicker}
									slotProps={{
										textField: {
											variant: "standard",
											margin: "normal",
										},
									}}
									disabled={travelArrangementsNotRequired}
								/>
							</Grid>
						</Condition>
					</Condition>
				</Grid>
			</Box>
		</>
	);
};
//#endregion

const SpeakerTravelSection: FC<SpeakerTravelSectionProps> = () => {
	const {
		state: { value: country },
	} = useScopedField<string>("country", { value: true });

	const {
		state: { value: postalCountry },
	} = useScopedField<string>("postalCountry", { value: true });

	const {
		state: { value: flightArrival },
	} = useScopedField<Date>("flightArrival", { value: true });
	const {
		state: { value: flightDeparture },
	} = useScopedField<Date>("flightDeparture", { value: true });

	const {
		state: { value: planeTicketsCurrent },
	} = useScopedField<PlaneTicket[]>("planeTickets", { value: true });

	const isDomesticDelegate =
		postalCountry?.toLowerCase() === "australia" ||
		postalCountry?.toLowerCase() === "new zealand" ||
		postalCountry?.toLowerCase() === "aus" ||
		postalCountry?.toLowerCase() === "nz" ||
		country?.toLowerCase() === "australia" ||
		country?.toLowerCase() === "new zealand" ||
		country?.toLowerCase() === "aus" ||
		country?.toLowerCase() === "nz";

	const disableFlightPreferences = (!!flightArrival && !!flightDeparture) ||
								!!(planeTicketsCurrent && planeTicketsCurrent.length > 0);

	return (
		<>
			<StyledAccordion elevation={4}>
				<StyledAccordianSummary
					expandIcon={
						<FontAwesomeIcon
							fixedWidth
							icon={faChevronDown}
							color="white"
						/>
					}
				>
					<Typography variant="h3" color="white">
						<b>Travel Information</b>
					</Typography>
				</StyledAccordianSummary>
				<StyledAccordionDetails>
					<Condition
						condition={hasCountryConditionFn}
						subscription={valueSubscription}
						mode={ConditionMode.Show}
					>
						<TravelSectionContent

							isDomesticDelegate={isDomesticDelegate}
							disableFlightPreferences={disableFlightPreferences}
						/>
					</Condition>

					<Condition
						condition={hasNoCountryConditionFn}
						subscription={valueSubscription}
						mode={ConditionMode.Show}
					>
						<Typography variant="body1">
							<i>Please fill out your country information first.</i>
						</Typography>
					</Condition>
				</StyledAccordionDetails>
			</StyledAccordion>
			<ErrorFields
				errorFields={[
					"frequentFlyerNumberQantas",
					"frequentFlyerNumberVirgin",
					"frequentFlyerNumberAirNZ",
					"frequentFlyerOther",
					"frequentFlyerNameOther",
					"frequentFlyerNumberOther",
					"seatingPreference",
					"checkedBaggage",
					"departureDate",
					"departureCity",
					"desiredArrivalTime",
					"returnDate",
					"returnCity",
					"desiredReturnTime",
					"travelWithCompanion",
					"companionFirstName",
					"companionLastName",
					"companionDietaryRequirements",
					"companionAttendFacultyDinner",
					"companionAttendConferenceDinner",
					"companionSharedAccommodation",
					"companionCheckInDate",
					"companionCheckOutDate",
					"companionTwinBeds",
					"cabCharge",
				]}
			/>
		</>
	);
};

export default SpeakerTravelSection;